import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import EdpiesLogo from '@/components/EdpiesLogo';
import SubjectThumbnail from '@/components/SubjectThumbnail';
import { 
  Home, 
  BookOpen, 
  GraduationCap, 
  Target, 
  Clock, 
  CheckCircle,
  ChevronRight,
  Brain,
  Award,
  Users,
  FileText,
  Zap,
  Play,
  Star,
  Sparkles,
  Trophy,
  RefreshCw,
  Volume2,
  Library,
  TrendingUp,
  Pencil,
  Flame,
  ArrowLeft,
  Lightbulb
} from 'lucide-react';

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

// Fun Math Game Component for 7+ Entry
const MathGame = ({ type = 'addition' }) => {
  const [num1, setNum1] = useState(0);
  const [num2, setNum2] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [isCorrect, setIsCorrect] = useState(null);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [showCelebration, setShowCelebration] = useState(false);

  const generateQuestion = () => {
    let n1, n2;
    if (type === 'addition') {
      n1 = Math.floor(Math.random() * 10) + 1;
      n2 = Math.floor(Math.random() * 10) + 1;
    } else if (type === 'subtraction') {
      n1 = Math.floor(Math.random() * 10) + 5;
      n2 = Math.floor(Math.random() * Math.min(n1, 10)) + 1;
    } else if (type === 'multiplication') {
      n1 = Math.floor(Math.random() * 5) + 1;
      n2 = Math.floor(Math.random() * 5) + 1;
    }
    setNum1(n1);
    setNum2(n2);
    setUserAnswer('');
    setIsCorrect(null);
  };

  useEffect(() => {
    generateQuestion();
  }, [type]);

  const getCorrectAnswer = () => {
    if (type === 'addition') return num1 + num2;
    if (type === 'subtraction') return num1 - num2;
    if (type === 'multiplication') return num1 * num2;
    return 0;
  };

  const getOperator = () => {
    if (type === 'addition') return '+';
    if (type === 'subtraction') return '-';
    if (type === 'multiplication') return '×';
    return '+';
  };

  const checkAnswer = () => {
    const correct = parseInt(userAnswer) === getCorrectAnswer();
    setIsCorrect(correct);
    if (correct) {
      setScore(score + 10);
      setStreak(streak + 1);
      if (streak >= 2) {
        setShowCelebration(true);
        setTimeout(() => setShowCelebration(false), 2000);
      }
      setTimeout(() => generateQuestion(), 1500);
    } else {
      setStreak(0);
    }
  };

  const getEmoji = () => {
    if (type === 'addition') return '➕';
    if (type === 'subtraction') return '➖';
    if (type === 'multiplication') return '✖️';
    return '🔢';
  };

  const animals = ['🐱', '🐶', '🐰', '🦊', '🐻', '🐼', '🐨', '🦁', '🐯', '🐸'];

  return (
    <Card className="bg-gradient-to-br from-yellow-400 via-orange-400 to-pink-400 border-0 shadow-xl overflow-hidden">
      <CardContent className="p-6 relative">
        {/* Celebration Animation */}
        <AnimatePresence>
          {showCelebration && (
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0 }}
              className="absolute inset-0 flex items-center justify-center bg-yellow-400/90 z-10"
            >
              <div className="text-center">
                <motion.div
                  animate={{ rotate: [0, 10, -10, 10, 0], scale: [1, 1.2, 1] }}
                  transition={{ duration: 0.5 }}
                  className="text-6xl mb-2"
                >
                  🎉
                </motion.div>
                <p className="text-2xl font-bold text-slate-900">Amazing Streak!</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Score Display */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2 bg-white/30 px-3 py-1 rounded-full">
            <Star className="h-5 w-5 text-yellow-200" />
            <span className="font-bold text-slate-900">{score} pts</span>
          </div>
          <div className="flex items-center gap-1">
            {[...Array(Math.min(streak, 5))].map((_, i) => (
              <motion.span
                key={i}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="text-2xl"
              >
                🔥
              </motion.span>
            ))}
          </div>
        </div>

        {/* Question Display with Animals */}
        <div className="bg-slate-100 backdrop-blur rounded-2xl p-6 mb-4">
          <div className="flex items-center justify-center gap-4 text-4xl font-bold text-slate-900">
            <motion.div 
              className="flex flex-wrap gap-1 max-w-20"
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
            >
              {[...Array(Math.min(num1, 10))].map((_, i) => (
                <span key={i} className="text-2xl">{animals[i % animals.length]}</span>
              ))}
            </motion.div>
            <span className="text-5xl">{getOperator()}</span>
            <motion.div 
              className="flex flex-wrap gap-1 max-w-20"
              initial={{ x: 20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
            >
              {[...Array(Math.min(num2, 10))].map((_, i) => (
                <span key={i} className="text-2xl">{animals[(i + 3) % animals.length]}</span>
              ))}
            </motion.div>
            <span className="text-5xl">=</span>
            <span className="text-5xl">❓</span>
          </div>
          <div className="text-center mt-4 text-2xl text-slate-600">
            {num1} {getOperator()} {num2} = ?
          </div>
        </div>

        {/* Answer Input */}
        <div className="space-y-3">
          <input
            type="number"
            value={userAnswer}
            onChange={(e) => setUserAnswer(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && userAnswer && checkAnswer()}
            className="w-full text-3xl text-center font-bold rounded-xl border-4 border-slate-200 bg-white/30 text-slate-900 placeholder-white/50 p-4 focus:outline-none focus:border-white"
            placeholder="Type your answer"
          />
          <Button
            onClick={checkAnswer}
            className="w-full bg-white text-orange-500 hover:bg-yellow-100 font-bold text-2xl py-6 rounded-xl shadow-lg hover:shadow-xl transition-all"
            disabled={!userAnswer}
          >
            ▶️ Submit Answer
          </Button>
        </div>

        {/* Feedback */}
        <AnimatePresence>
          {isCorrect !== null && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className={`mt-4 p-4 rounded-xl text-center text-xl font-bold ${
                isCorrect ? 'bg-green-500 text-white' : 'bg-red-400 text-white'
              }`}
            >
              {isCorrect ? (
                <span>🎉 Correct! Well done! 🌟</span>
              ) : (
                <span>Try again! The answer is {getCorrectAnswer()} 💪</span>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* New Question Button */}
        <Button
          onClick={generateQuestion}
          variant="ghost"
          className="w-full mt-4 text-slate-900 hover:bg-slate-100"
        >
          <RefreshCw className="mr-2 h-4 w-4" /> New Question
        </Button>
      </CardContent>
    </Card>
  );
};

// Educational Videos for 7+ Entry
const educationalVideos = [
  {
    id: 1,
    title: 'Addition & Subtraction for Kids',
    description: 'Learn addition and subtraction up to 20 with Kids Academy!',
    thumbnail: '🎵',
    embedId: 'VOMt_H7Q684',
    duration: '18:49'
  },
  {
    id: 2,
    title: 'Count to 100 with Jack Hartmann',
    description: 'Fun counting exercise song for kids!',
    thumbnail: '🔢',
    embedId: '0TgLtF3PMOc',
    duration: '4:47'
  },
  {
    id: 3,
    title: 'Shapes Song for Children',
    description: 'Learn shapes with the Silly Shapes Song!',
    thumbnail: '🔷',
    embedId: '8Z9ntb--ZV4',
    duration: '3:04'
  },
  {
    id: 4,
    title: 'Big Numbers Song - Count to 100',
    description: 'Fun counting song by The Singing Walrus!',
    thumbnail: '🔢',
    embedId: 'bGetqbqDVaA',
    duration: '3:54'
  }
];

// Resource data for each educational stage
const resourceData = {
  'seven-plus': {
    id: 'seven-plus',
    name: '7+ Entry',
    fullName: '7+ Entry Examinations',
    icon: '🌟',
    color: 'from-blue-500 to-cyan-500',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30',
    textColor: 'text-blue-600',
    description: 'Prepare your child for independent school entry at age 7 with comprehensive resources tailored to young learners.',
    overview: 'The 7+ examination is an entry assessment for Year 3 at independent and selective schools. These exams typically assess core skills in English and Mathematics, along with reasoning abilities appropriate for 6-7 year olds.',
    subjects: [
      { name: 'English', topics: ['Reading Comprehension', 'Phonics', 'Vocabulary', 'Creative Writing', 'Grammar Basics'] },
      { name: 'Mathematics', topics: ['Number Recognition', 'Basic Addition & Subtraction', 'Shapes & Patterns', 'Measurement', 'Problem Solving'] },
      { name: 'Reasoning', topics: ['Verbal Reasoning', 'Non-Verbal Reasoning', 'Pattern Recognition', 'Spatial Awareness'] }
    ],
    examFormat: [
      { aspect: 'Duration', detail: '45-60 minutes per paper' },
      { aspect: 'Question Types', detail: 'Multiple choice, short answer, and practical tasks' },
      { aspect: 'Assessment Areas', detail: 'Literacy, Numeracy, and Reasoning' },
      { aspect: 'Interview', detail: 'Many schools include an informal interview or activity session' }
    ],
    tips: [
      'Start preparation early with fun, engaging activities',
      'Focus on building confidence rather than pressure',
      'Practice reading aloud regularly',
      'Use games to develop numerical skills',
      'Encourage curiosity and asking questions'
    ],
    paperCount: { mock: 50, practice: 50 }
  },
  'eleven-plus': {
    id: 'eleven-plus',
    name: '11+ Entry',
    fullName: '11+ Entry Examinations',
    icon: '📚',
    color: 'from-purple-500 to-indigo-500',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/30',
    textColor: 'text-purple-600',
    description: 'Comprehensive preparation for grammar school and independent school entry examinations at age 11.',
    overview: 'The 11+ is a selective entrance examination for grammar schools and many independent schools. Different exam boards (CEM, GL Assessment, ISEB) use varying formats, but all assess English, Mathematics, Verbal Reasoning, and Non-Verbal Reasoning.',
    subjects: [
      { name: 'English', topics: ['Comprehension', 'Grammar & Punctuation', 'Vocabulary', 'Creative Writing', 'Spelling'] },
      { name: 'Mathematics', topics: ['Arithmetic', 'Fractions & Decimals', 'Geometry', 'Data Handling', 'Problem Solving', 'Algebra Basics'] },
      { name: 'Verbal Reasoning', topics: ['Word Patterns', 'Analogies', 'Code Breaking', 'Logic Problems', 'Vocabulary Skills'] },
      { name: 'Non-Verbal Reasoning', topics: ['Shape Sequences', 'Spatial Reasoning', 'Pattern Recognition', 'Mirror Images', 'Matrices'] }
    ],
    examFormat: [
      { aspect: 'Duration', detail: '45-60 minutes per paper' },
      { aspect: 'Question Types', detail: 'Multiple choice (GL/CEM) or standard format (ISEB)' },
      { aspect: 'Exam Boards', detail: 'CEM, GL Assessment, ISEB, Sutton SET' },
      { aspect: 'Timing', detail: 'Usually taken in September-November of Year 6' }
    ],
    tips: [
      'Familiarise with the specific exam board format',
      'Practice time management with timed papers',
      'Build vocabulary through regular reading',
      'Work on mental arithmetic speed',
      'Use practice papers to identify weak areas'
    ],
    paperCount: { mock: 50, practice: 50 }
  },
  'year-9': {
    id: 'year-9',
    name: 'Year 9',
    fullName: 'Year 9 Curriculum',
    icon: '📖',
    color: 'from-teal-500 to-cyan-500',
    bgColor: 'bg-teal-500/10',
    borderColor: 'border-teal-500/30',
    textColor: 'text-teal-600',
    description: 'Bridge the gap between Key Stage 3 and GCSE with targeted Year 9 resources across all core subjects.',
    overview: 'Year 9 is a crucial transition year where students consolidate Key Stage 3 learning and begin preparing for GCSE content. This year often determines GCSE subject choices and sets the foundation for exam success.',
    subjects: [
      { name: 'Mathematics', topics: ['Algebra', 'Geometry', 'Statistics', 'Number Theory', 'Ratio & Proportion'] },
      { name: 'English', topics: ['Literature Analysis', 'Essay Writing', 'Language Techniques', 'Poetry', 'Drama'] },
      { name: 'Science', topics: ['Biology Fundamentals', 'Chemistry Basics', 'Physics Principles', 'Scientific Method'] },
      { name: 'History', topics: ['World Wars', 'British Empire', 'Medieval History', 'Source Analysis', 'Historical Enquiry'] },
      { name: 'Geography', topics: ['Physical Geography', 'Human Geography', 'Fieldwork Skills', 'Map Reading', 'Climate & Weather'] }
    ],
    examFormat: [
      { aspect: 'Assessment Type', detail: 'End of year exams and continuous assessment' },
      { aspect: 'Focus', detail: 'Building foundations for GCSE study' },
      { aspect: 'Key Skills', detail: 'Extended writing, analysis, and problem-solving' },
      { aspect: 'Subject Choices', detail: 'Options selection typically made during Year 9' }
    ],
    tips: [
      'Start thinking about GCSE subject choices early',
      'Develop strong study habits now',
      'Practice extended writing across subjects',
      'Begin creating revision resources',
      'Seek help early if struggling with any topic'
    ],
    paperCount: { mock: 50, practice: 50 }
  },
  'year-10': {
    id: 'year-10',
    name: 'Year 10',
    fullName: 'Year 10 GCSE Foundation',
    icon: '📝',
    color: 'from-indigo-500 to-purple-500',
    bgColor: 'bg-indigo-500/10',
    borderColor: 'border-indigo-500/30',
    textColor: 'text-indigo-600',
    description: 'Begin your GCSE journey with comprehensive Year 10 resources covering all exam board specifications.',
    overview: 'Year 10 marks the official start of GCSE courses. Students study their chosen subjects in depth, building the knowledge and skills needed for final examinations in Year 11.',
    subjects: [
      { name: 'Mathematics', topics: ['Advanced Algebra', 'Trigonometry', 'Statistics', 'Geometry Proofs', 'Functions'] },
      { name: 'English Language', topics: ['Reading Analysis', 'Creative Writing', 'Transactional Writing', 'Spoken Language'] },
      { name: 'English Literature', topics: ['Shakespeare', 'Modern Texts', 'Poetry Anthology', 'Unseen Poetry'] },
      { name: 'Sciences', topics: ['Biology', 'Chemistry', 'Physics', 'Required Practicals', 'Scientific Investigation'] },
      { name: 'Geography', topics: ['Tectonic Hazards', 'Weather Hazards', 'Climate Change', 'Ecosystems', 'Urban Issues'] }
    ],
    examFormat: [
      { aspect: 'Exam Boards', detail: 'AQA, Edexcel, OCR, WJEC' },
      { aspect: 'Assessment', detail: 'Mix of exams and controlled assessments' },
      { aspect: 'Grade Scale', detail: '9-1 grading system' },
      { aspect: 'Coursework', detail: 'Some subjects include non-exam assessment' }
    ],
    tips: [
      'Stay on top of coursework deadlines',
      'Create a revision timetable early',
      'Use past papers from your specific exam board',
      'Attend extra help sessions when needed',
      'Balance study with relaxation'
    ],
    paperCount: { mock: 50, practice: 50 }
  },
  'gcse': {
    id: 'gcse',
    name: 'GCSE',
    fullName: 'GCSE Examinations',
    icon: '🎯',
    color: 'from-green-500 to-emerald-500',
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500/30',
    textColor: 'text-green-600',
    description: 'Excel in your GCSE examinations with our comprehensive revision materials and exam practice papers.',
    overview: 'GCSEs (General Certificate of Secondary Education) are the main qualifications taken by students aged 14-16 in England, Wales, and Northern Ireland. Results are crucial for sixth form, college, and apprenticeship applications.',
    subjects: [
      { name: 'Mathematics', topics: ['Number', 'Algebra', 'Ratio & Proportion', 'Geometry', 'Probability & Statistics'] },
      { name: 'English Language', topics: ['Explorations in Creative Reading', 'Writers\' Viewpoints', 'Creative Writing'] },
      { name: 'English Literature', topics: ['Shakespeare', '19th Century Novel', 'Modern Texts', 'Poetry'] },
      { name: 'Combined Science', topics: ['Cell Biology', 'Atomic Structure', 'Energy', 'Waves', 'Ecology'] },
      { name: 'Triple Science', topics: ['Biology', 'Chemistry', 'Physics - Separate Awards'] }
    ],
    examFormat: [
      { aspect: 'Exam Period', detail: 'May-June of Year 11' },
      { aspect: 'Grade Boundaries', detail: 'Grade 4 is standard pass, Grade 5 is strong pass' },
      { aspect: 'Tiers', detail: 'Foundation (grades 1-5) and Higher (grades 4-9)' },
      { aspect: 'Results', detail: 'Released in August' }
    ],
    tips: [
      'Create a detailed revision timetable',
      'Practice with past papers under timed conditions',
      'Focus on understanding mark schemes',
      'Use active revision techniques',
      'Look after your mental health during exam season'
    ],
    paperCount: { mock: 50, practice: 50 }
  },
  'a-levels': {
    id: 'a-levels',
    name: 'A-Levels',
    fullName: 'A-Level Examinations',
    icon: '🎓',
    color: 'from-orange-500 to-red-500',
    bgColor: 'bg-orange-500/10',
    borderColor: 'border-orange-500/30',
    textColor: 'text-orange-600',
    description: 'Achieve top grades in your A-Level examinations with advanced study materials and expert guidance.',
    overview: 'A-Levels (Advanced Level qualifications) are subject-based qualifications typically taken over two years by students aged 16-18. They are the main route to university education in the UK.',
    subjects: [
      { name: 'Mathematics', topics: ['Pure Mathematics', 'Statistics', 'Mechanics', 'Further Mathematics'] },
      { name: 'Physics', topics: ['Mechanics', 'Electricity', 'Waves', 'Quantum Physics', 'Astrophysics'] },
      { name: 'Chemistry', topics: ['Physical Chemistry', 'Inorganic Chemistry', 'Organic Chemistry', 'Analysis'] },
      { name: 'Biology', topics: ['Biological Molecules', 'Cells', 'Genetics', 'Ecology', 'Control Systems'] },
      { name: 'English Literature', topics: ['Drama', 'Poetry', 'Prose', 'Literary Theory', 'Coursework'] }
    ],
    examFormat: [
      { aspect: 'Duration', detail: '2-year linear courses' },
      { aspect: 'Assessment', detail: 'Terminal exams at end of Year 13' },
      { aspect: 'Grade Scale', detail: 'A*-E grading' },
      { aspect: 'UCAS Points', detail: 'A* = 56, A = 48, B = 40, C = 32' }
    ],
    tips: [
      'Understand the specification thoroughly',
      'Develop independent study skills',
      'Engage with wider reading around your subjects',
      'Practice exam technique regularly',
      'Start university applications early'
    ],
    paperCount: { mock: 50, practice: 50 }
  }
};

const allStages = Object.values(resourceData);

const Resources = ({ isAuthenticated }) => {
  const navigate = useNavigate();
  const { stageId } = useParams();
  const [activeStage, setActiveStage] = useState(stageId || 'all');

  useEffect(() => {
    if (stageId && resourceData[stageId]) {
      setActiveStage(stageId);
    } else if (!stageId) {
      setActiveStage('all');
    }
  }, [stageId]);

  const currentResource = activeStage !== 'all' ? resourceData[activeStage] : null;

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/categories');
    } else {
      navigate('/auth');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-slate-100/50 to-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <motion.div 
              className="flex items-center gap-2 cursor-pointer"
              whileHover={{ scale: 1.05 }}
              onClick={() => navigate('/')}
            >
              <EdpiesLogo size="sm" />
              <span className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-indigo-400 bg-clip-text text-transparent">Edpies</span>
            </motion.div>
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/')} className="text-slate-600 hover:text-slate-900 hover:bg-slate-100">
                Home
              </Button>
              <Button variant="ghost" onClick={() => navigate('/about')} className="text-slate-600 hover:text-slate-900 hover:bg-slate-100">
                About Us
              </Button>
              <Button variant="ghost" className="text-slate-900 bg-slate-100">
                Resources
              </Button>
              <Button variant="ghost" onClick={() => navigate('/contact')} className="text-slate-600 hover:text-slate-900 hover:bg-slate-100">
                Contact
              </Button>
              <Button variant="ghost" onClick={() => navigate('/faq')} className="text-slate-600 hover:text-slate-900 hover:bg-slate-100">
                FAQ
              </Button>
              <Button onClick={handleGetStarted} className="bg-gradient-to-r from-purple-600 to-indigo-600">
                {isAuthenticated ? 'Go to Dashboard' : 'Get Started'}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-12 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl md:text-6xl font-bold text-slate-900 mb-6" 
            style={{ fontFamily: 'Space Grotesk' }}
          >
            Learning <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">Resources</span>
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-slate-500 max-w-3xl mx-auto"
          >
            Comprehensive study materials and exam preparation resources for every stage of your educational journey
          </motion.p>
        </div>
      </section>

      {/* Educational Stages Grid */}
      <section className="px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-8"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Space Grotesk' }}>
              Choose Your Learning Stage
            </h2>
            <p className="text-slate-500">Select an academic stage to explore tailored resources</p>
          </motion.div>
          
          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4"
          >
            {allStages.map((stage, idx) => (
              <motion.div 
                key={stage.id} 
                variants={fadeInUp}
                whileHover={{ scale: 1.05, y: -5 }}
                whileTap={{ scale: 0.95 }}
              >
                <Card 
                  className={`bg-slate-100 border-slate-200/50 hover:${stage.borderColor} transition-all cursor-pointer group ${activeStage === stage.id ? `ring-2 ring-offset-2 ring-offset-slate-900 ${stage.borderColor.replace('border-', 'ring-')}` : ''}`}
                  onClick={() => { 
                    setActiveStage(stage.id); 
                    navigate(`/resources/${stage.id}`);
                    setTimeout(() => {
                      document.getElementById('stage-content')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 100);
                  }}
                  data-testid={`stage-tile-${stage.id}`}
                >
                  <CardContent className="p-4 md:p-6 text-center">
                    <motion.div 
                      className="text-3xl md:text-4xl mb-3"
                      animate={{ y: [0, -3, 0] }}
                      transition={{ duration: 2, repeat: Infinity, delay: idx * 0.2 }}
                    >
                      {stage.icon}
                    </motion.div>
                    <div className={`h-1 w-8 mx-auto rounded-full bg-gradient-to-r ${stage.color} mb-3 group-hover:w-full transition-all duration-300`}></div>
                    <p className="text-slate-900 font-semibold text-sm md:text-base">{stage.name}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Eds-Library Section */}
      <section className="px-4 py-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/10 via-teal-900/10 to-cyan-900/10"></div>
        <div className="max-w-6xl mx-auto relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl p-6 md:p-8"
          >
            <div className="flex flex-col lg:flex-row items-center gap-8">
              {/* Left Content */}
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-3 rounded-xl">
                    <Library className="h-6 w-6 text-slate-900" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Space Grotesk' }}>Eds-Library</h3>
                    <span className="text-xs bg-emerald-500/20 text-emerald-600 px-2 py-0.5 rounded-full">New Feature</span>
                  </div>
                </div>
                
                <p className="text-lg text-emerald-600 italic mb-4">
                  "An intelligent, interactive learning space that grows with the student—academically and confidently."
                </p>
                
                <p className="text-slate-500 mb-6">
                  Beyond practice papers, Eds-Library offers a curated reading experience with AI-powered vocabulary tools, 
                  comprehension quizzes, and an intelligent tutor that explains concepts at your level.
                </p>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  {[
                    { icon: BookOpen, label: 'Smart Reading' },
                    { icon: Brain, label: 'AI Tutor' },
                    { icon: Target, label: 'Vocabulary Builder' },
                    { icon: TrendingUp, label: 'Progress Tracking' }
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                      <item.icon className="h-4 w-4 text-emerald-600" />
                      <span className="text-slate-600">{item.label}</span>
                    </div>
                  ))}
                </div>

                <Button 
                  onClick={handleGetStarted}
                  className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
                  data-testid="eds-library-cta"
                >
                  Explore Eds-Library <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </div>

              {/* Right Preview Card */}
              <div className="w-full lg:w-80 shrink-0">
                <div className="bg-slate-100 rounded-xl p-4 space-y-3">
                  {/* Word of Day Mini */}
                  <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Sparkles className="h-3 w-3 text-amber-600" />
                      <span className="text-amber-600 text-xs font-medium">Word of the Day</span>
                    </div>
                    <p className="text-slate-900 font-semibold">Eloquent</p>
                    <p className="text-slate-500 text-xs">fluent or persuasive in speaking or writing</p>
                  </div>

                  {/* Reading Preview */}
                  <div className="bg-slate-200/30 rounded-lg p-3">
                    <p className="text-slate-900 text-sm font-medium mb-1">The Secret Garden Gate</p>
                    <p className="text-slate-500 text-xs line-clamp-2">
                      The old iron gate stood at the far end of the overgrown garden...
                    </p>
                    <p className="text-emerald-600 text-xs mt-1">📖 2 min read</p>
                  </div>

                  {/* Levels */}
                  <div className="flex gap-2">
                    <span className="text-xs bg-purple-500/20 text-purple-600 px-2 py-1 rounded-full">11+ Ready</span>
                    <span className="text-xs bg-green-500/20 text-green-600 px-2 py-1 rounded-full">GCSE Level</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Eds Whiteboard X Section */}
      <section className="px-4 py-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-violet-900/10 via-purple-900/10 to-indigo-900/10"></div>
        <div className="max-w-6xl mx-auto relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-gradient-to-br from-violet-50 to-fuchsia-50 border border-violet-200 rounded-2xl p-6 md:p-8"
          >
            <div className="flex flex-col lg:flex-row items-center gap-8">
              {/* Left Content */}
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-gradient-to-r from-violet-500 to-purple-500 p-3 rounded-xl">
                    <Pencil className="h-6 w-6 text-slate-900" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Space Grotesk' }}>Eds Whiteboard X</h3>
                    <div className="flex items-center gap-2">
                      <span className="text-xs bg-violet-500/20 text-violet-600 px-2 py-0.5 rounded-full">Premium</span>
                      <span className="text-xs bg-amber-500/20 text-amber-600 px-2 py-0.5 rounded-full">NEW</span>
                    </div>
                  </div>
                </div>
                
                <p className="text-lg text-violet-600 italic mb-4">
                  "Daily expert challenges from top tutors to stretch your thinking and build mastery."
                </p>
                
                <p className="text-slate-500 mb-6">
                  A daily challenge space where tutors post high-quality "stretch" problems. 
                  Get 3 attempts with progressive hints and AI-powered guidance to help you grow.
                </p>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  {[
                    { icon: Pencil, label: 'Visual Challenges' },
                    { icon: Flame, label: 'Tiered Difficulty' },
                    { icon: Lightbulb, label: 'Smart Hints' },
                    { icon: Trophy, label: 'Track Progress' }
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                      <item.icon className="h-4 w-4 text-violet-600" />
                      <span className="text-slate-600">{item.label}</span>
                    </div>
                  ))}
                </div>

                <Button 
                  onClick={handleGetStarted}
                  className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
                  data-testid="whiteboard-x-cta"
                >
                  Try Whiteboard X <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </div>

              {/* Right Preview Card */}
              <div className="w-full lg:w-80 shrink-0">
                <div className="bg-slate-100 rounded-xl p-4 space-y-3">
                  {/* Challenge Preview */}
                  <div className="bg-violet-500/10 border border-violet-500/20 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Brain className="h-4 w-4 text-amber-600" />
                        <span className="text-amber-600 text-xs font-medium">Stretch Challenge</span>
                      </div>
                      <span className="text-slate-500 text-xs">Day 42</span>
                    </div>
                    <p className="text-slate-900 font-semibold text-sm mb-1">Modular Arithmetic</p>
                    <p className="text-slate-500 text-xs">Find the remainder when 7^123 is divided by 5...</p>
                  </div>

                  {/* Attempt Tracker */}
                  <div className="bg-slate-200/30 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-slate-900 text-sm font-medium">Your Attempts</span>
                      <span className="text-green-600 text-xs">2/3 left</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center text-red-600 text-sm">✗</div>
                      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 text-sm">2</div>
                      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 text-sm">3</div>
                      <div className="ml-auto flex items-center gap-1 text-amber-600 text-xs">
                        <Lightbulb className="h-3 w-3" />
                        Hint ready
                      </div>
                    </div>
                  </div>

                  {/* Tier Badges */}
                  <div className="flex gap-2">
                    <span className="text-xs bg-green-500/20 text-green-600 px-2 py-1 rounded-full flex items-center gap-1"><Star className="h-3 w-3" /> Core</span>
                    <span className="text-xs bg-amber-500/20 text-amber-600 px-2 py-1 rounded-full flex items-center gap-1"><Flame className="h-3 w-3" /> Stretch</span>
                    <span className="text-xs bg-purple-500/20 text-purple-600 px-2 py-1 rounded-full flex items-center gap-1"><Trophy className="h-3 w-3" /> Elite</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Eds UniGuide™ Section */}
      <section className="px-4 py-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-900/10 via-purple-900/10 to-pink-900/10"></div>
        <div className="max-w-6xl mx-auto relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-gradient-to-br from-indigo-50 to-blue-50 border border-indigo-200 rounded-2xl p-6 md:p-8"
          >
            <div className="flex flex-col lg:flex-row items-center gap-8">
              {/* Left Content */}
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 p-3 rounded-xl">
                    <GraduationCap className="h-6 w-6 text-slate-900" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Space Grotesk' }}>Eds UniGuide™</h3>
                    <div className="flex items-center gap-2">
                      <span className="text-xs bg-indigo-500/20 text-indigo-600 px-2 py-0.5 rounded-full">Year 13</span>
                      <span className="text-xs bg-green-500/20 text-green-600 px-2 py-0.5 rounded-full">NEW</span>
                    </div>
                  </div>
                </div>
                
                <p className="text-lg text-indigo-600 italic mb-4">
                  "From Ambition to Admission — Your AI-powered university counsellor"
                </p>
                
                <p className="text-slate-500 mb-6">
                  Navigate your entire UCAS journey with personalized university matching, AI-generated personal statements, 
                  mock interview practice with feedback, and predictive analytics to maximize your chances of admission.
                </p>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  {[
                    { icon: Users, label: 'Profile Builder' },
                    { icon: Target, label: 'Uni Matching' },
                    { icon: FileText, label: 'Personal Statement' },
                    { icon: Brain, label: 'Mock Interviews' }
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                      <item.icon className="h-4 w-4 text-indigo-600" />
                      <span className="text-slate-600">{item.label}</span>
                    </div>
                  ))}
                </div>

                <Button 
                  onClick={() => isAuthenticated ? navigate('/pathways') : navigate('/auth')}
                  className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:from-indigo-700 hover:via-purple-700 hover:to-pink-700"
                  data-testid="uniguide-cta"
                >
                  Start UniGuide Journey <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </div>

              {/* Right Preview Card */}
              <div className="w-full lg:w-80 shrink-0">
                <div className="bg-slate-100 rounded-xl p-4 space-y-3">
                  {/* Success Probability Preview */}
                  <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="h-4 w-4 text-green-600" />
                        <span className="text-green-600 text-xs font-medium">Predictive Analytics</span>
                      </div>
                    </div>
                    <p className="text-slate-900 font-semibold text-sm mb-1">Success Probability</p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full w-3/4 bg-gradient-to-r from-green-500 to-emerald-400 rounded-full"></div>
                      </div>
                      <span className="text-green-600 text-sm font-bold">75%</span>
                    </div>
                  </div>

                  {/* Universities Preview */}
                  <div className="bg-slate-200/30 rounded-lg p-3">
                    <p className="text-slate-900 text-sm font-medium mb-2">Your Shortlist</p>
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-600">University of Cambridge</span>
                        <span className="text-amber-600">Reach</span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-600">UCL</span>
                        <span className="text-green-600">Match</span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-600">University of Leeds</span>
                        <span className="text-blue-600">Safety</span>
                      </div>
                    </div>
                  </div>

                  {/* Feature Badges */}
                  <div className="flex flex-wrap gap-2">
                    <span className="text-xs bg-indigo-500/20 text-indigo-600 px-2 py-1 rounded-full">36+ Universities</span>
                    <span className="text-xs bg-purple-500/20 text-purple-600 px-2 py-1 rounded-full">AI Coaching</span>
                    <span className="text-xs bg-pink-500/20 text-pink-600 px-2 py-1 rounded-full">UCAS Ready</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stage Selection Tabs */}
      <section className="px-4 pb-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            <Button
              variant={activeStage === 'all' ? 'default' : 'outline'}
              onClick={() => { setActiveStage('all'); navigate('/resources'); }}
              className={activeStage === 'all' ? 'bg-gradient-to-r from-purple-600 to-indigo-600' : 'border-slate-200 text-slate-600'}
            >
              All Stages
            </Button>
            {allStages.map((stage) => (
              <Button
                key={stage.id}
                variant={activeStage === stage.id ? 'default' : 'outline'}
                onClick={() => { 
                  setActiveStage(stage.id); 
                  navigate(`/resources/${stage.id}`);
                  setTimeout(() => {
                    document.getElementById('stage-content')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 100);
                }}
                className={activeStage === stage.id ? `bg-gradient-to-r ${stage.color}` : 'border-slate-200 text-slate-600'}
                data-testid={`resource-tab-${stage.id}`}
              >
                {stage.icon} {stage.name}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Content Area */}
      <section id="stage-content" className="px-4 pb-24">
        <div className="max-w-6xl mx-auto">
          {activeStage === 'all' ? (
            /* All Stages Overview */
            <motion.div 
              key="all-stages-grid"
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {allStages.map((stage, idx) => (
                <motion.div key={stage.id} variants={fadeInUp}>
                  <Card 
                    className={`bg-slate-100 border-slate-200/50 hover:${stage.borderColor} transition-all cursor-pointer group h-full`}
                    onClick={() => { 
                      setActiveStage(stage.id); 
                      navigate(`/resources/${stage.id}`);
                      setTimeout(() => {
                        document.getElementById('stage-content')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }, 100);
                    }}
                    data-testid={`resource-card-${stage.id}`}
                  >
                    <CardHeader>
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-4xl">{stage.icon}</span>
                        <div>
                          <CardTitle className="text-slate-900 group-hover:text-purple-600 transition-colors">{stage.name}</CardTitle>
                          <CardDescription className="text-slate-500">{stage.fullName}</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-slate-500 mb-4">{stage.description}</p>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-slate-500">
                          <BookOpen className="inline h-4 w-4 mr-1" /> {stage.subjects.length} Subjects
                        </span>
                      </div>
                      <div className={`mt-4 h-1 w-0 group-hover:w-full bg-gradient-to-r ${stage.color} rounded-full transition-all duration-300`}></div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          ) : (
            /* Individual Stage Detail */
            currentResource && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                {/* Stage Header */}
                <div className={`${currentResource.bgColor} ${currentResource.borderColor} border rounded-2xl p-8 mb-8`}>
                  <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
                    <span className="text-6xl">{currentResource.icon}</span>
                    <div className="flex-1">
                      <h2 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Space Grotesk' }}>
                        {currentResource.fullName}
                      </h2>
                      <p className="text-slate-600 text-lg">{currentResource.description}</p>
                    </div>
                    <Button 
                      onClick={handleGetStarted}
                      size="lg"
                      className={`bg-gradient-to-r ${currentResource.color}`}
                      data-testid="start-practicing-btn"
                    >
                      Start Practicing <ChevronRight className="ml-2 h-5 w-5" />
                    </Button>
                  </div>
                </div>

                {/* Overview */}
                <Card className="bg-slate-100 border-slate-200/50 mb-8">
                  <CardHeader>
                    <CardTitle className="text-slate-900 flex items-center gap-2">
                      <BookOpen className={`h-5 w-5 ${currentResource.textColor}`} /> Overview
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-600 text-lg leading-relaxed">{currentResource.overview}</p>
                  </CardContent>
                </Card>

                {/* Subjects Grid */}
                <h3 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <GraduationCap className={`h-6 w-6 ${currentResource.textColor}`} /> Subjects Covered
                </h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                  {currentResource.subjects.map((subject, idx) => (
                    <Card key={idx} className="bg-slate-100 border-slate-200/30">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg text-slate-900 flex items-center gap-3">
                          <SubjectThumbnail subject={subject.name} size="sm" animate={false} />
                          {subject.name}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-wrap gap-2">
                          {subject.topics.map((topic, tidx) => (
                            <span key={tidx} className={`text-xs px-2 py-1 rounded-full ${currentResource.bgColor} ${currentResource.textColor}`}>
                              {topic}
                            </span>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Exam Format & Tips */}
                <div className="grid md:grid-cols-2 gap-8 mb-8">
                  {/* Exam Format */}
                  <Card className="bg-slate-100 border-slate-200/50">
                    <CardHeader>
                      <CardTitle className="text-slate-900 flex items-center gap-2">
                        <Target className={`h-5 w-5 ${currentResource.textColor}`} /> Exam Format
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {currentResource.examFormat.map((item, idx) => (
                          <div key={idx} className="flex items-start gap-3">
                            <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${currentResource.color} mt-2`}></div>
                            <div>
                              <p className="text-slate-900 font-semibold">{item.aspect}</p>
                              <p className="text-slate-500 text-sm">{item.detail}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Top Tips */}
                  <Card className="bg-slate-100 border-slate-200/50">
                    <CardHeader>
                      <CardTitle className="text-slate-900 flex items-center gap-2">
                        <Zap className={`h-5 w-5 ${currentResource.textColor}`} /> Top Tips for Success
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {currentResource.tips.map((tip, idx) => (
                          <div key={idx} className="flex items-start gap-3">
                            <CheckCircle className={`h-5 w-5 ${currentResource.textColor} flex-shrink-0 mt-0.5`} />
                            <p className="text-slate-600">{tip}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </motion.div>
            )
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-slate-200">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <EdpiesLogo size="sm" />
              <span className="text-xl font-bold bg-gradient-to-r from-purple-400 to-indigo-400 bg-clip-text text-transparent">Edpies</span>
            </div>
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" className="text-purple-600 hover:text-purple-600" onClick={() => navigate('/brand')}>
                Brand Guidelines
              </Button>
              <Button variant="ghost" size="sm" className="text-slate-500 hover:text-slate-900" onClick={() => navigate('/privacy')}>
                Privacy
              </Button>
              <Button variant="ghost" size="sm" className="text-slate-500 hover:text-slate-900" onClick={() => navigate('/terms')}>
                Terms
              </Button>
            </div>
            <p className="text-slate-500 text-sm">© 2025 Edpies. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Resources;
